import React, { useState, useEffect, Fragment, useRef } from "react";
import { useAppContext } from "../helpers/account-context";
import { ICONS, useOnClickOutside } from "../helpers/helper";
import {
  getParameters,
  requestCurrentGeo,
  requestPlacePredictions,
  requestGeocode,
  mapParser,
  syncLocationStates,
} from "../helpers/sharedUtils";
import { suggestLocations, CBAnalytics } from "../helpers/apiEndpoints";
import { LocationSuggestions, AddressSuggestions } from "./Search/Suggestions";

const sugPrefix = "sug";

// https://stackoverflow.com/questions/49671434/is-there-any-way-to-customize-googles-auto-complete-address-result
// https://developers.google.com/maps/documentation/javascript/examples/geocoding-place-id
// https://developers.google.com/maps/documentation/places/web-service/autocomplete#maps_http_places_autocomplete_fr-rb

const SearchForm = (props) => {
  const currentParams = getParameters();
  const {account, initedData, getLocale} = useAppContext()
  const {
    enabledFilterAll,
    onChangeSetting,
    onSelectSearch,
    geocoder,
    filters,
    autoSuggestion
  } = props;
  const [query, setQuery] = useState('');
  const [queryChanged, setQueryChanged] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestedLocations, setSuggestedLocations] = useState([]);

  useEffect(() => {
    console.log('SearchForm', filters.search)
    setQuery(filters.search);
  }, [filters.search]);

  const handleRequestGeo = (e) => {
    e.preventDefault();
    requestCurrentGeo(geocoder).then(({ address, lat, lng }) => {
      setQuery(address ? address : `${lat || ""}, ${lng || ""}`);
      onSelectSearch({
        lat,
        lng,
        search: address,
        current: 1
      });
    }).catch((err) => {
      console.log('geo error', err)
      window.alert('Allow sharing your current location to use this feature.')
    });
  };

  const handleQueryChange = (e) => {
    e.preventDefault();
    const {
      target: { value },
    } = e;
    setQuery(value || "");
    if (value.length > 0 && value.length < 3) return false;
    setIsLoading(true);
    setQueryChanged(!!value && value.length > 0);
    const { search_restriction, suggestion_type } = account;
    const componentRestrictions = search_restriction && search_restriction.split(",").length > 0 ? {country: search_restriction.split(",")} : {};
    // suggest location only
    if(true || suggestion_type !== 'location') {
      requestPlacePredictions(autoSuggestion, {
        input: value,
        componentRestrictions,
      }).then((data) => {
        setSuggestions(data);
        setIsLoading(false);
      });
    }

    // suggest address only
    if(suggestion_type !== 'address') {
      suggestLocations(account.uid, { query: value }).then(({ resources }) => {
        setSuggestedLocations(resources);
        setIsLoading(false);
      });
    }
  };

  const handleSelectLocation = (loc) => {
    setQuery(loc.name);
    setQueryChanged(false);
    onSelectSearch({
      location: loc.uid,
      search: loc.name,
      lat: loc.lat,
      lng: loc.lng,
    });
    const { search, ...newParams } = currentParams;
    syncLocationStates({
      ...newParams,
      location: loc.uid,
    })
   
    if(!initedData.preview) {
      CBAnalytics.click(account.uid, {
        location_name: loc.name,
        location_id: loc.num,
        clicked_url: window.location.href,
        suggested: 1
      })
    }
  };

  const handleSelectPlace = async (suggestion) => {
    setQuery(suggestion.description);
    let selectedLoc = {};
    let resultComps = {};
    if(suggestion.geometry) {
      selectedLoc = {
        lng: suggestion.geometry.coordinates[0],
        lat: suggestion.geometry.coordinates[1],
        search: suggestion.description
      }
      resultComps = mapParser.components(suggestion)     
    } else {
      const {results} = await requestGeocode(geocoder, { placeId: suggestion.place_id });
      selectedLoc = {...results[0].geometry.location, search: results[0].formatted_address};
      if (typeof selectedLoc.lat === "function") {
        selectedLoc.lat = selectedLoc.lat()
        selectedLoc.lng = selectedLoc.lng()
      }
      resultComps = mapParser.components(results[0])

    }
    console.log('resultComps', resultComps) 

    onSelectSearch({
      ...selectedLoc
    });
    
      CBAnalytics.search(account.uid, {
        ...resultComps,
        ...selectedLoc,
        state: resultComps.province,
        place_id: suggestion.place_id,
        query: suggestion.description
      })
  

    setQueryChanged(false);
    const { location, ...newParams } = currentParams;
    syncLocationStates({
      ...newParams,
      search: suggestion.description,
    })
   
  };
  const handleClearQuery = (e) => {
    // TODO
    e.preventDefault();
    setQuery("");
    onSelectSearch({search: ''});
  };

  const handleArrowKeys = (event) => {
    const { target } = event;
    if (
      target.classList &&
      !target.classList.contains("cbmap-search-input") &&
      !target.classList.contains("cbmap-suggestion-item")
    )
      return false;

    if (document.getElementById(target.id))
      document.getElementById(target.id).blur();

    // arrowUp
    if (event.keyCode === 38) {
      const currentId = +target.id.split("-").pop() - 1;
      if (document.getElementById(`${sugPrefix}-${+currentId}`))
        document.getElementById(`${sugPrefix}-${+currentId}`).focus();
    } else if (event.keyCode === 40) {
      // arrowDown
      const currentId = +target.id.split("-").pop() + 1;
      if (document.getElementById(`${sugPrefix}-${+currentId}`))
        document.getElementById(`${sugPrefix}-${+currentId}`).focus();
    } else if (event.keyCode === 13) {
      // enter
      if (target.dataset.locationId) {
        const loc = suggestedLocations.find(
          (loc) => loc.uid === target.dataset.locationId
        );
        if (loc) handleSelectLocation(loc);
      } else if (target.dataset.placeId) {
        const place = suggestions.find(
          (pl) => pl.place_id === target.dataset.placeId
        );
        if (place) handleSelectPlace(place);
      }

      setQueryChanged(false);
    }
  };

  const suggestionRef = useRef();

  useOnClickOutside(suggestionRef, (e) => {
    setQueryChanged(false);
  });

  // https://codepen.io/smithsuth/pen/VvYzZP
  const {hiddenFilters, geolocation} = initedData
  const isHiddenFilters = !!hiddenFilters && hiddenFilters.indexOf('all') >= 0;
  return (

    <div className="cbmap-search-query" onKeyDown={handleArrowKeys} ref={suggestionRef}>
      <div action="#" className="cbmap-search-form">
        {
          !isHiddenFilters && enabledFilterAll && <button
          className="cbmap-filter-btn"
          type="button"
          onClick={(e) => onChangeSetting("all")}
          title={getLocale("show_filters")}
          dangerouslySetInnerHTML={{ __html: ICONS.FILTER }}
        />
        }
        {
          (isHiddenFilters || !enabledFilterAll) && <button
          className="cbmap-filter-btn"
          type="button"
          dangerouslySetInnerHTML={{ __html: ICONS.SEARCH }}
        />
        }
        
        <div className="cbmap-input-wrapper">
          <input
            type="text"
            placeholder={getLocale("query_message")}
            name="address"
            className="cbmap-search-input"
            value={`${query || ""}`}
            onChange={handleQueryChange}
            disabled={!autoSuggestion}
            autoComplete="off"
          />
          <div className="cbmap-search-icons">
            {isLoading && (
              <button
                className="cbmap-loader-btn"
                title={getLocale('loading')}
                dangerouslySetInnerHTML={{ __html: ICONS.LOADER }}
              />
            )}
            {!isLoading && query && query.length && (
              <button
                className="cbmap-loader-btn"
                onClick={handleClearQuery}
                title={getLocale('clear_search')}
                dangerouslySetInnerHTML={{ __html: ICONS.CLEAR }}
              />
            )}
            {
              geolocation && <button
              className="cbmap-geo-btn"
              dangerouslySetInnerHTML={{ __html: ICONS.GEO }}
              title={getLocale('get_current_location')}
              onClick={handleRequestGeo}
            />
            }
            
          </div>
        </div>
      </div>
      <Fragment>
        {queryChanged && (
          <div className="cbmap-suggestions">
            {
              suggestedLocations.length === 0 && suggestions.length === 0 && <div className="cbmap-suggestion-item not-found">{getLocale('not_found')}</div>
            }
            <LocationSuggestions
              suggestions={suggestedLocations}
              onSelect={handleSelectLocation}
            />
            <AddressSuggestions
              suggestions={suggestions}
              startIdx={suggestedLocations.length + 1}
              onSelect={handleSelectPlace}
            />
          </div>
        )}
      </Fragment>
    </div>
  
  );
};

export default SearchForm;
